import useI18n from '@/hooks/useI18n';
import Loading from '../Loading';
import { useCallback, useState } from 'react';

interface DragDropComponentProps extends React.HTMLAttributes<HTMLDivElement> {
  imagePreview: string | null;
  setImagePreview: (value: string | null) => void;
  fileName: string | null;
  setFileName: (value: string) => void;
  onFileSelect?: (file: File) => void;
  error?: string;
  isLoading: boolean;
  disabled?: boolean;
}

const DragDropComponent: React.FC<DragDropComponentProps> = ({
  imagePreview,
  setImagePreview,
  fileName,
  setFileName,
  onFileSelect,
  error,
  isLoading,
  disabled = false,
  ...props
}) => {
  const { t } = useI18n();
  const [isDragging, setIsDragging] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const file = e.target.files?.[0];
    if (!file) return;
    handleFile(file);
  };

  const handleFile = useCallback(
    (file: File) => {
      if (disabled || !onFileSelect) return;
      const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) return;

      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      onFileSelect(file);
    },
    [disabled, onFileSelect, setFileName, setImagePreview],
  );

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="flex flex-col items-center">
      {isLoading && <Loading size="md" fullScreen />}

      {/* Main container - now acts as the label itself */}
      <label
        htmlFor={!disabled ? 'brand-logo-upload' : undefined}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={(e) => !disabled && e.preventDefault()}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-3xl p-4 flex flex-col
          items-center justify-center h-[172px] w-[251px] gap-1
          ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
        `}
      >
        {/* Hidden file input */}
        <input
          id="brand-logo-upload"
          type="file"
          accept="image/png, image/jpeg, image/svg+xml"
          className="hidden"
          disabled={disabled}
          onChange={handleImageChange}
        />

        {imagePreview ? (
          <div className="border border-[#B3B3B2] rounded-[16px] p-1 mt-2">
            <img
              src={imagePreview}
              alt={String(t('campaign.altTexts.brandLogo'))}
              className="max-h-[120px] max-w-[240px] object-contain rounded-[12px]"
            />
          </div>
        ) : (
          <>
            <img
              src="../assets/uploadBlueIcon.svg"
              alt={String(t('campaign.altTexts.brandLogo'))}
              className="max-h-[48px] max-w-[48px] object-contain"
            />
            <p className="text-sm text-center">
              {t(disabled ? 'common.logoLocked' : 'common.dragDropText')}
            </p>
          </>
        )}
        {disabled && (
          <span className="absolute bottom-2 text-xs text-gray-600"></span>
        )}
      </label>
      {error && <div className="text-sm text-red-500">{error}</div>}
    </div>
  );
};

export default DragDropComponent;
